/* global axios */
import ApiClient from './ApiClient';

class CampaignsAPI extends ApiClient {
  constructor() {
    super('campaigns', { accountScoped: true });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Override base get() to support pagination, sorting, and archive filter params
  get(params = {}) {
    return axios.get(this.url, { params });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Get campaign status from Marketing Analytics API
  getStatus(id) {
    return axios.get(`${this.url}/${id}/status`);
  }

  // NOTICE: KAPTION-INTEGRATION
  // Get campaign analytics from backend (which calls Marketing Analytics API)
  getAnalytics(id, options = {}) {
    const params = {
      type: options.type,
      start_date: options.startDate,
      end_date: options.endDate,
      granularity: options.granularity,
      limit: options.limit,
      offset: options.offset,
      status: options.status,
    };
    // Remove undefined params
    Object.keys(params).forEach(key => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });
    return axios.get(`${this.url}/${id}/analytics`, { params });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Convenience methods for specific analytics types
  getMetricsTotals(id, options = {}) {
    return this.getAnalytics(id, { ...options, type: 'totals' });
  }

  getMetricsTimeline(id, options = {}) {
    return this.getAnalytics(id, { ...options, type: 'timeline' });
  }

  getCampaignEvents(id, options = {}) {
    return this.getAnalytics(id, { ...options, type: 'events' });
  }

  getCampaignMetrics(id) {
    return this.getAnalytics(id, {});
  }

  // NOTICE: KAPTION-INTEGRATION
  // Aggregate Campaign Reports dashboard data.
  // type: 'totals' | 'timeline' | 'top_campaigns'
  // GET /api/v1/accounts/:account_id/campaigns/reports
  getReportsData(options = {}) {
    const params = {
      type: options.type,
      campaign_type: options.campaignType,
      start_date: options.startDate,
      end_date: options.endDate,
      granularity: options.granularity,
      inbox_id: options.inboxId,
      campaign_ids: options.campaignIds,
      include_archived: options.includeArchived,
    };
    // Remove undefined/empty params (note: `false` is a valid include_archived
    // value and is intentionally preserved by this check).
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        delete params[key];
      }
    });
    return axios.get(`${this.url}/reports`, { params });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Campaign Reporting exports — aggregate (Path 2).
  // POST /api/v1/accounts/:account_id/campaigns/reports/export
  // body: { format, group_by, filters }
  exportAggregate({ format, groupBy, filters } = {}) {
    return axios.post(`${this.url}/reports/export`, {
      format,
      group_by: groupBy,
      filters,
    });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Campaign Reporting exports — single-campaign delivery events (Path 3).
  // POST /api/v1/accounts/:account_id/campaigns/:id/events/export
  // body: { format, status, columns, max_rows }
  exportCampaignEvents(id, { format, status, columns, maxRows } = {}) {
    return axios.post(`${this.url}/${id}/events/export`, {
      format,
      status,
      columns,
      max_rows: maxRows,
    });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Campaign Reporting exports — general/multi-campaign delivery events (Path 3).
  // POST /api/v1/accounts/:account_id/campaigns/events/export
  // body: { format, status, columns, max_rows, filters }
  exportGeneralEvents({ format, status, columns, maxRows, filters } = {}) {
    return axios.post(`${this.url}/events/export`, {
      format,
      status,
      columns,
      max_rows: maxRows,
      filters,
    });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Campaign Reporting exports — poll an async export job's status.
  // GET /api/v1/accounts/:account_id/campaigns/exports/:id
  getExportStatus(id) {
    return axios.get(`${this.url}/exports/${id}`);
  }

  // NOTICE: KAPTION-INTEGRATION
  // Archive a campaign
  archive(id) {
    return axios.patch(`${this.url}/${id}/archive`);
  }

  // NOTICE: KAPTION-INTEGRATION
  // Unarchive a campaign
  unarchive(id) {
    return axios.patch(`${this.url}/${id}/unarchive`);
  }
}

export default new CampaignsAPI();
